"use client";

import React from "react";
import { ArrowRight, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const PINNED_POST_IDS = ["1122", "1006", "1142", "1160", "1173", "1131"];
function PostsConsumer() {
  const {
    posts: allPosts,
    loading,
    error,
    setQuery
  } = usePosts();
  const posts = React.useMemo(() => {
    if (!allPosts) return [];
    const filtered = allPosts.filter(post => post.categories?.some(cat => typeof cat === "object" ? cat.id === 250 : cat === 250));
    const pinnedMap = new Map(filtered.map(p => [String(p.id), p]));
    const pinnedPosts = PINNED_POST_IDS.map(id => pinnedMap.get(id)).filter((p): p is (typeof filtered)[number] => p !== undefined);
    const pinnedIdSet = new Set(PINNED_POST_IDS);
    const remainingPosts = filtered.filter(p => !pinnedIdSet.has(String(p.id)));
    return [...pinnedPosts, ...remainingPosts];
  }, [allPosts]);
  return <div data-section-id="111439" className="flex flex-col gap-10">
      {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-full">
              <div className="rounded-lg bg-[#E0E0E0]/20 animate-pulse h-96 w-full" />
            </div>)}
        </div> : error ? <div className="flex flex-col items-center justify-center py-20 text-center text-destructive bg-destructive/5 rounded-lg border border-destructive/10">
          <CircleAlert className="size-10 mb-4 opacity-80" />
          <h3 className="text-lg font-normal mb-2 font-sans text-[#4A5D23]">Fehler beim Laden</h3>
          <p className="text-sm font-light opacity-80 max-w-md font-sans text-[#333333]">Die aktuellen Berichte konnten leider nicht geladen werden. Bitte versuchen Sie es später erneut.</p>
        </div> : posts.length === 0 ? <div className="flex flex-col items-center justify-center py-24 text-center bg-[#F9F9F9] rounded-lg border border-[#E0E0E0]">
          <p className="text-lg text-[#333333] font-light font-sans">Derzeit liegen keine Ankündigungen in dieser Kategorie vor.</p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, i) => <div key={post.id} data-index={i} className="h-full">
              <Link to={post.link || "#"} className="block h-full group outline-none focus-visible:ring-2 focus-visible:ring-[#2C5F63] rounded-lg">
                <Card className="h-full overflow-hidden border-[#E0E0E0] bg-[#F9F9F9] hover:border-[#2C5F63]/30 transition-all duration-500 rounded-lg p-0 gap-0 shadow-none">
                  <div className="relative aspect-video overflow-hidden bg-[#FAF6F1]">
                    <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587884/img/generic_news_placeholder.png"} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
                    <div className="absolute top-4 left-4 z-10">
                      <Badge className="bg-[#2C5F63]/80 hover:bg-[#2C5F63] backdrop-blur-sm border-white/10 text-white font-sans font-medium tracking-wide shadow-none transition-colors rounded-sm">
                        {post.categories && post.categories.length > 0 ? post.categories[0].name : "Aktuelles"}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6 flex flex-col gap-4">
                    <time className="text-xs font-normal text-[#333333]/60 uppercase tracking-widest font-sans">
                      {post.date ? new Date(post.date).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                }) : "Kürzlich"}
                    </time>
                    <h3 className="text-xl md:text-2xl font-sans font-normal text-[#4A5D23] leading-snug group-hover:text-[#2C5F63] transition-colors line-clamp-3">
                      {post.title}
                    </h3>
                    {post.excerpt && <div className="text-[#333333] text-sm font-light leading-relaxed line-clamp-2 font-sans" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} />}
                    <div className="mt-2 flex items-center text-[#2C5F63] font-normal text-sm overflow-hidden pt-2 font-sans uppercase tracking-wider">
                      <ArrowRight className="size-4 mr-2 -ml-6 opacity-0 transition-all duration-300 ease-out group-hover:ml-0 group-hover:opacity-100" />
                      <span className="transition-transform duration-300 ease-out group-hover:translate-x-1">Mehr erfahren...</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>)}
        </div>}
    </div>;
}
export default function BerichteNeuigkeiten() {
  const wp_query_params = {
    per_page: 20,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="aktuelles-aus-der-schule" className="py-24 bg-[#FAF6F1] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6F1] via-[#4A5D23]/5 to-[#FAF6F1] pointer-events-none" />
      
      <div className="container relative mx-auto px-4 md:px-6">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#4A5D23] mb-6 font-sans">
            Aktuelles <span className="italic text-[#60664D]">aus der Schule</span>
          </h2>
        </div>
        
        <PostsProvider wp_query={{
        per_page: 20,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <PostsConsumer />
        </PostsProvider>
      </div>
    </section>;
}